import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request: attach stored access token ──────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Auth store persists to localStorage — read directly to avoid circular import
  try {
    const raw = localStorage.getItem('maternally-auth');
    if (raw) {
      const { state } = JSON.parse(raw);
      if (state?.accessToken) {
        config.headers.Authorization = `Bearer ${state.accessToken}`;
      }
    }
  } catch {
    // ignore
  }
  return config;
});

// ─── Response: silent token refresh on 401 ────────────────
let isRefreshing = false;
let waitQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(err: unknown, token: string | null) {
  waitQueue.forEach(p => (err ? p.reject(err) : p.resolve(token!)));
  waitQueue = [];
}

api.interceptors.response.use(
  res => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(normalise(error));
    }

    // Don't retry auth endpoints themselves
    if (original.url?.includes('/auth/')) {
      return Promise.reject(normalise(error));
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        waitQueue.push({ resolve, reject });
      }).then(token => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const raw = localStorage.getItem('maternally-auth');
      const { state } = JSON.parse(raw ?? '{}');
      const refreshToken = state?.refreshToken;

      if (!refreshToken) throw new Error('No refresh token');

      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });

      // Update persisted store
      const stored = JSON.parse(localStorage.getItem('maternally-auth') ?? '{}');
      stored.state = {
        ...stored.state,
        accessToken:  data.accessToken,
        refreshToken: data.refreshToken,
      };
      localStorage.setItem('maternally-auth', JSON.stringify(stored));

      api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
      processQueue(null, data.accessToken);

      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return api(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      // Clear auth and redirect to login
      localStorage.removeItem('maternally-auth');
      window.location.href = '/login';
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Normalise errors for consistent UI consumption ───────
export interface ApiError {
  message: string;
  status:  number;
  code?:   string;
  /** The raw response body, for callers that need more than the message. */
  data?:   Record<string, unknown>;
}

function normalise(error: AxiosError): ApiError & Error {
  const status  = error.response?.status ?? 0;
  const data    = error.response?.data as Record<string, unknown> | undefined;
  const message = (data?.error as string) ?? (data?.message as string) ?? error.message ?? 'An unexpected error occurred';
  const code    = data?.code as string | undefined;

  const err = new Error(message) as Error & ApiError;
  err.status  = status;
  // Keep the response body. Flattening every failure to a message string threw
  // away the structured fields a caller sometimes needs — the sign-in screen has
  // to tell "wrong password" from "this account is pending deletion, offer to
  // restore it", and that distinction lives in the body, not the text.
  err.data    = data;
  err.message = message;
  if (code) err.code = code;
  return err;
}
